import { useState, useEffect } from 'react';
import { Grid, Box, Modal } from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import ButtonCustom from 'components/ButtonCustom/ButtonCustom';
import showToast from 'components/Toast/Toast';
import { getCommunityFullCalendar, editWeekPost } from 'utils/requests/communities';
import * as S from './style';
import zeScriptImage from '../../../../assets/ze_script.png';
import { useParams } from 'react-router-dom';
import BuildScriptForm from '../forms/BuildScriptForm';
import EditScriptForm from '../forms/EditScriptForm';

const formatOptions = [
  { value: 'video', label: 'Vídeo' },
  { value: 'reel', label: 'Reel' },
  { value: 'story', label: 'Story' },
  { value: 'post', label: 'Post' },
  { value: 'carousel', label: 'Carrossel' }
];

const ScriptLibrary = () => {
  const { communityId } = useParams();

  const [showViewModal, setShowViewModal] = useState(false);
  const [openConfirmDeletionModal, setOpenConfirmDeletionModal] = useState(false);
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptToDelete, setScriptToDelete] = useState(null);
  const [savedScripts, setSavedScripts] = useState([]);
  const [contentOptions, setContentOptions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getFormatName = (format) => {
    const formatOption = formatOptions.find(option => option.value === format);
    return formatOption ? formatOption.label : format;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const organizeScriptsByCompletion = (scripts) => {
    const sortedScripts = scripts;

    const upcomingScripts = sortedScripts.filter(script => !script.completed);
    const completedScripts = sortedScripts.filter(script => script.completed);
    return { upcomingScripts, completedScripts };
  };

  const handleViewScript = (script) => {
    setSelectedScript(script);
    setShowViewModal(true);
  };

  const handleEditScript = (script) => {
    setSelectedScript(script);
    setShowViewModal(false);
    setShowEditModal(true);
  };

  const copyScript = (script) => {
    navigator.clipboard.writeText(script.content).then(() => {
      showToast("Roteiro copiado para a área de transferência!", "success");
    }).catch(() => {
      showToast("Erro ao copiar roteiro", "error");
    });
  };

  const handleDeleteScript = (scriptId) => {
    setScriptToDelete(scriptId);
    setOpenConfirmDeletionModal(true);
  };

  const handleDelete = async () => {
    if (!scriptToDelete) return;

    try {
      const scriptToRemove = savedScripts.find(script => script.id === scriptToDelete);
      if (!scriptToRemove) return;

      const updatedScriptData = {
        tone: scriptToRemove.originalScriptData.tone,
        title: scriptToRemove.originalScriptData.title || scriptToRemove.title,
        format: scriptToRemove.originalScriptData.format,
        objective: scriptToRemove.originalScriptData.objective,
        description: scriptToRemove.originalScriptData.description
      };

      const updateResponse = await editWeekPost(scriptToDelete, {
        script_data: JSON.stringify(updatedScriptData)
      });

      if (updateResponse) {
        setSavedScripts(prev => prev.filter(script => script.id !== scriptToDelete));
        setOpenConfirmDeletionModal(false);
        setScriptToDelete(null);
        setShowViewModal(false);
        showToast("Roteiro deletado com sucesso!", "success");
        fetchScriptsData();
      } else {
        showToast("Erro ao deletar roteiro", "error");
      }
    } catch (error) {
      console.error("Erro ao deletar roteiro:", error);
      showToast("Erro ao deletar roteiro", "error");
    }
  };

  const cancelDelete = () => {
    setOpenConfirmDeletionModal(false);
    setScriptToDelete(null);
  };

  const fetchScriptsData = async () => {
    setIsLoading(true);
    try {
      const calendarData = await getCommunityFullCalendar(communityId);
      
      if (!calendarData?.calendars?.length) {
        setContentOptions([]);
        setSavedScripts([]);
        return;
      }

      const calendar = calendarData.calendars[0];
      if (!calendar.weeks?.length) {
        setContentOptions([]);
        setSavedScripts([]);
        return;
      }

      const options = [];
      const scripts = [];
      
      calendar.weeks.forEach((week, weekIndex) => {
        if (week.posts?.length) {
          week.posts.forEach((post, postIndex) => {
            const scriptData = typeof post.script_data === 'string' ? JSON.parse(post.script_data) : post.script_data;
            const hasScript = scriptData?.generated_script;
            const isCompleted = scriptData?.completed ?? false;
            
            if (!hasScript && !isCompleted) {
              options.push({
                id: post.id ?? "",
                label: `Semana ${weekIndex + 1}, Post ${postIndex + 1} - ${post.post_title}` ?? "",
                post_title: post.post_title ?? "",
                description: scriptData?.description ?? "",
                format: scriptData?.format ?? "",
                objective: scriptData?.objective ?? "",
                tone: scriptData?.tone ?? "",
                weekId: week.id ?? "",
                weekIndex: weekIndex,
                postIndex: postIndex
              });
            } else if (hasScript) {
              scripts.push({
                id: post.id ?? "",
                postId: post.id ?? "",
                title: post.post_title ?? "",
                content: scriptData.generated_script.content ?? "",
                format: scriptData.generated_script.format ?? "",
                objective: scriptData.generated_script.objective ?? "",
                observation: scriptData.generated_script.observation ?? "",
                createdAt: scriptData.generated_script.createdAt ?? "",
                tags: [`Semana ${weekIndex + 1} - Post ${postIndex + 1}`, getFormatName(scriptData.format ?? ""), getFormatName(scriptData.generated_script.format ?? "")],
                completed: isCompleted,
                originalScriptData: scriptData,
                weekIndex: weekIndex,
                postIndex: postIndex
              });
            }
          });
        }
      });
      
      setContentOptions(options);
      setSavedScripts(scripts);
    } catch (error) {
      if (error.status !== 404) {
        showToast("Erro ao buscar dados dos roteiros", "error");
      }
      console.error("Erro ao buscar dados dos roteiros:", error);
      setContentOptions([]);
      setSavedScripts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchScriptsData();
  }, [communityId]);

  const renderScriptCard = (script) => (
    <Grid item xs={12} md={4} key={script.id}>
      <S.ScriptLibraryCard 
        completed={script.completed}
        onClick={() => handleViewScript(script)}
      >
        {script.completed && (
          <S.ScriptLibraryCompletedBadge>
            <S.ScriptLibraryCompletedCheck>✓</S.ScriptLibraryCompletedCheck>
          </S.ScriptLibraryCompletedBadge>
        )}
        
        <S.ScriptLibraryCardTitle completed={script.completed}>
          {script.title} - {getFormatName(script.format)}
        </S.ScriptLibraryCardTitle>
        
        <S.ScriptLibraryCardDate completed={script.completed}>
          {formatDate(script.createdAt)}
        </S.ScriptLibraryCardDate>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {(script.tags || []).map((tag, index) => (
            <S.ScriptLibraryTagChip
              key={index}
              label={tag}
              size="small"
              completed={script.completed}
            />
          ))}
        </Box>
      </S.ScriptLibraryCard>
    </Grid>
  );

  const renderEmptyState = () => (
    <>
      <Grid container justifyContent='space-between'>
        <S.FormTitleGrid container flexDirection='column' item md={7}>
          <p>Roteiros</p>
          <p className='subTitle'>Sua biblioteca de roteiros personalizados</p>
        </S.FormTitleGrid>
      </Grid>

      <S.ScriptLibraryEmptyContainer>
        <S.ScriptLibraryEmptyImage src={zeScriptImage} alt="Nenhum roteiro criado" />
        <S.ScriptLibraryEmptyTitle>
          Nenhum roteiro criado, comece agora!
        </S.ScriptLibraryEmptyTitle>
        
        <S.ScriptLibraryCreateButton
          variant="contained"
          onClick={() => setShowCreateModal(true)}
          disabled={contentOptions.length === 0}
        >
          {contentOptions.length === 0 ? 'Comece criando seu calendário' : 'Começar a gerar seus roteiros'}
        </S.ScriptLibraryCreateButton>
      </S.ScriptLibraryEmptyContainer>
    </>
  );

  const renderScriptsList = () => {
    const { upcomingScripts, completedScripts } = organizeScriptsByCompletion(savedScripts);
    const scriptsToShow = showAllUpcoming ? upcomingScripts : upcomingScripts.slice(0, 6);

    return (
      <>
        <S.ScriptLibraryHeader>
          <Grid container justifyContent='space-between'>
            <S.FormTitleGrid container flexDirection='column' item md={7}>
              <p>Roteiros</p>
              <p className='subTitle'>Sua biblioteca de roteiros personalizados</p>
            </S.FormTitleGrid>
          </Grid>
          <S.EditButton
            variant="contained"
            onClick={() => setShowCreateModal(true)}
            disabled={contentOptions.length === 0}
          >
            {contentOptions.length === 0 ? 'Sem Conteúdos Disponíveis' : 'Criar Novo Roteiro'}
          </S.EditButton>
        </S.ScriptLibraryHeader>

        {upcomingScripts.length > 0 && (
          <S.ScriptLibrarySection>
            <S.ScriptLibrarySectionTitle variant="h6">
              A Seguir
            </S.ScriptLibrarySectionTitle>
            
            <Grid container spacing={2}>
              {scriptsToShow.map(renderScriptCard)}
            </Grid>
            
            {upcomingScripts.length > 6 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <S.ScriptLibraryShowMoreButton
                  onClick={() => setShowAllUpcoming(!showAllUpcoming)}
                >
                  {!showAllUpcoming ? 'Ver mais' : 'Ver menos'}
                </S.ScriptLibraryShowMoreButton>
              </Box>
            )}
          </S.ScriptLibrarySection>
        )}

        {completedScripts.length > 0 && (
          <S.ScriptLibrarySection>
            <S.ScriptLibrarySectionTitle variant="h6">
              Anteriores
            </S.ScriptLibrarySectionTitle>
            
            <Grid container spacing={2}>
              {completedScripts.map(renderScriptCard)}
            </Grid>
          </S.ScriptLibrarySection>
        )}
      </>
    );
  };

  return (
    <S.CalendarContainer>
      {isLoading ? (
        <div>
          Carregando...
        </div>
      ) : savedScripts.length === 0 ? renderEmptyState() : renderScriptsList()}
      <Modal
        open={showViewModal}
        onClose={() => setShowViewModal(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <S.ScriptLibraryViewModal>
          <S.ScriptLibraryViewModalCloseButton
            aria-label="close"
            onClick={() => setShowViewModal(false)}
          >
            <CloseIcon />
          </S.ScriptLibraryViewModalCloseButton>
          <S.ScriptLibraryViewModalHeader>
            <S.ScriptLibraryViewModalTitle variant="h5">
              {selectedScript?.title?.split(' - ')[0]}
            </S.ScriptLibraryViewModalTitle>
            <S.ScriptLibraryViewModalDate variant="body2">
              Criado em: {selectedScript ? formatDate(selectedScript.createdAt) : ''}
            </S.ScriptLibraryViewModalDate>
          </S.ScriptLibraryViewModalHeader>
          {selectedScript && (
            <>
              <S.ScriptLibraryViewModalContent>
                <S.ScriptLibraryViewModalText variant="body1">
                  {selectedScript.content}
                </S.ScriptLibraryViewModalText>
              </S.ScriptLibraryViewModalContent>
              <S.ScriptLibraryViewModalActions>
                <S.ScriptLibraryViewModalActionsContainer>
                  {!selectedScript.completed && (
                    <>
                      <S.ScriptLibraryDeleteButton
                        variant="outlined"
                        onClick={() => handleDeleteScript(selectedScript.id)}
                      >
                        <DeleteIcon sx={{ mr: 1 }} />
                        Deletar
                      </S.ScriptLibraryDeleteButton>
                      <S.ScriptLibraryCopyButton
                        variant="contained"
                        onClick={() => handleEditScript(selectedScript)}
                      >
                        Editar
                      </S.ScriptLibraryCopyButton>
                    </>
                  )}
                  <S.ScriptLibraryCopyButton
                    variant="contained"
                    onClick={() => copyScript(selectedScript)}
                  >
                    Copiar Roteiro
                  </S.ScriptLibraryCopyButton>
                </S.ScriptLibraryViewModalActionsContainer>
              </S.ScriptLibraryViewModalActions>
            </>
          )}
        </S.ScriptLibraryViewModal>
      </Modal>
      <Modal
        open={openConfirmDeletionModal}
        onClose={cancelDelete}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <S.ScriptLibraryConfirmModal>
          <S.ScriptLibraryConfirmModalCloseButton
            aria-label="close"
            onClick={cancelDelete}
          >
            <CloseIcon />
          </S.ScriptLibraryConfirmModalCloseButton>
          <S.ScriptLibraryConfirmModalContent container flexDirection="column" gap="20px">
            <S.ScriptLibraryConfirmModalTitle>Confirmar exclusão</S.ScriptLibraryConfirmModalTitle>
            <S.ScriptLibraryConfirmModalText>
              Deseja excluir esse roteiro? Essa ação é irreversível
            </S.ScriptLibraryConfirmModalText>
          </S.ScriptLibraryConfirmModalContent>
          <S.ScriptLibraryConfirmModalActions container gap="15px" justifyContent="center">
            <Grid item lg={3}>
              <ButtonCustom
                label="Cancelar"
                onClick={cancelDelete}
                sx={{ background: "linear-gradient(180deg, #818181 0%, #2a2020 155.15%)" }}
              />
            </Grid>
            <Grid item lg={3}>
              <ButtonCustom label="Confirmar" onClick={handleDelete} />
            </Grid>
          </S.ScriptLibraryConfirmModalActions>
        </S.ScriptLibraryConfirmModal>
      </Modal>
      <BuildScriptForm
        openModal={showCreateModal}
        closeModal={() => setShowCreateModal(false)}
        contentOptions={contentOptions}
        refetch={fetchScriptsData}
      />
      <EditScriptForm
        openModal={showEditModal}
        closeModal={() => setShowEditModal(false)}
        selectedScript={selectedScript}
        refetch={fetchScriptsData}
      />
    </S.CalendarContainer>
  );
};

export default ScriptLibrary;